import { Card, ErrorFallback } from "materia"
import { useMemo, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { Document, Page } from "react-pdf"
import type {
  DocumentCallback,
  OnLoadProgressArgs,
  PageCallback,
} from "react-pdf/dist/shared/types"
import { modifiers } from "ui-tools"
import { createPlaythrough } from "../../graphql/createPlaythrough"
import { pollForProgressChange } from "../ConfettiBanner/checkForProgressChange"
import { Navigation, Outline, Toolbar } from "./components"
import { BASE_PAGE_WIDTH, PDFContextProvider, usePDFContext } from "./providers"

import "react-pdf/dist/Page/AnnotationLayer.css" // allows to render clickable links
import "react-pdf/dist/Page/TextLayer.css" // allows to render selectable text

import s from "./PDFViewer.module.scss"

type PDFViewerProps = {
  fileUrl: string
  downloadUrl: string | null
  productId: string
  title: string
}
export const PDFViewer = ({
  fileUrl,
  downloadUrl,
  productId,
  title,
}: PDFViewerProps) => {
  return (
    <PDFContextProvider>
      <ErrorBoundary
        fallbackRender={({ error }) => <PDFViewerError error={error} />}
      >
        <Viewer
          fileUrl={fileUrl}
          downloadUrl={downloadUrl}
          productId={productId}
          title={title}
        />
      </ErrorBoundary>
    </PDFContextProvider>
  )
}

const Viewer = ({ fileUrl, downloadUrl, productId, title }: PDFViewerProps) => {
  const [loadingPercentage, setLoadingPercentage] = useState<number>(0)
  const [fileError, setFileError] = useState<Error | null>(null)
  const [isInitialLoadCompleted, setIsInitialLoadCompleted] =
    useState<boolean>(false) // used to fit page to view when PDF is first opened

  const {
    containerRef,
    currentPageNumber,
    zoomFactor,
    numberOfPagesRef,
    setInitialPageSize,
    navigateToPage,
    setPageAspectRatio,
  } = usePDFContext()

  const pdfDocumentOptions = useMemo(
    () => ({
      wasmUrl: "/wasm/",
    }),
    []
  )

  // Page will rerender when either page number or zoom factor changes.
  // This rerender will cause it to blink. We don't want that, so we
  // detect the rerender and display a fake page with old parameters
  // until a new one is rendered.
  const [previousPageNumber, setPreviousPageNumber] =
    useState<number>(currentPageNumber)
  const [previousZoomFactor, setPreviousZoomFactor] =
    useState<number>(zoomFactor)

  const isRerendering =
    previousPageNumber &&
    previousZoomFactor &&
    (previousPageNumber !== currentPageNumber ||
      previousZoomFactor !== zoomFactor)

  const onDocumentLoadProgress = ({ loaded, total }: OnLoadProgressArgs) => {
    const percentage = Math.round((loaded / total) * 100)
    setLoadingPercentage(percentage)
  }

  const onDocumentLoadSuccess = ({ numPages }: DocumentCallback) => {
    numberOfPagesRef.current = numPages
  }

  const onDocumentLoadError = (error: Error) => {
    setFileError(error)
  }

  const onDocumentItemClick = ({ pageNumber }: { pageNumber: number }) => {
    navigateToPage(pageNumber)
  }

  const onPageLoadSuccess = ({ width, height }: PageCallback) => {
    const aspectRatio = width / height
    setPageAspectRatio(aspectRatio)

    if (!isInitialLoadCompleted) {
      setIsInitialLoadCompleted(true)
      setInitialPageSize(aspectRatio)
      createPlaythrough(productId)
      // Pick up the backend's async score processing. The before-snapshot was
      // seeded by ViewerModal when the product modal opened (PDFViewer only
      // renders inside ViewerModal).
      pollForProgressChange(productId)
    }
  }

  const onPageRenderSuccess = () => {
    // Store the parameters of the current page so when we resize it
    // or navigate to other one, we can use them to display a fake
    // page with old parameters until a new one finishes rendering
    setPreviousPageNumber(currentPageNumber)
    setPreviousZoomFactor(zoomFactor)
  }

  if (fileError) return <PDFViewerError error={fileError} />

  return (
    <Document
      className={s.document}
      externalLinkTarget="_blank"
      file={fileUrl}
      key={fileUrl}
      loading={<Progress percentage={loadingPercentage} />}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadProgress={onDocumentLoadProgress}
      onLoadError={onDocumentLoadError}
      onItemClick={onDocumentItemClick}
      options={pdfDocumentOptions}
    >
      <Toolbar fileName={title} downloadUrl={downloadUrl} />

      <div className={s.layout}>
        <Outline />

        <div className={s.pages} ref={containerRef}>
          {isRerendering ? (
            <Page
              key={`page_${previousPageNumber}_${previousZoomFactor}`}
              pageNumber={previousPageNumber}
              width={BASE_PAGE_WIDTH}
              scale={previousZoomFactor}
              className={modifiers(s, "page", { blurWhileRendering: true })}
            />
          ) : null}

          <Page
            key={`page_${currentPageNumber}_${zoomFactor}`}
            pageNumber={currentPageNumber}
            width={BASE_PAGE_WIDTH}
            scale={zoomFactor}
            onLoadSuccess={onPageLoadSuccess}
            onRenderSuccess={onPageRenderSuccess}
            className={modifiers(s, "page", {
              hiddenWhileRendering: isRerendering,
            })}
          />

          <Navigation />
        </div>
      </div>
    </Document>
  )
}

const Progress = ({ percentage }: { percentage: number }) => (
  <div className={s.progressContainer}>
    <progress className={s.progress} value={percentage} max="100" />
  </div>
)

const PDFViewerError = ({ error }: { error: Error }) => {
  const { t } = useTranslation()

  const getErrorDetails = () => {
    switch (error.name) {
      case "InvalidPDFException":
        return {
          title: t("pdf_error.INVALID_PDF_FILE"),
          message: t("pdf_error.CANT_BE_DISPLAYED"),
        }
      default:
        return {
          title: t("error.GENERIC"),
          message: t("pdf_error.GENERIC"),
        }
    }
  }

  const { title, message } = getErrorDetails()

  return (
    <Card>
      <ErrorFallback
        error={error}
        title={title}
        message={message}
        featureTag="pdf_viewer"
      />
    </Card>
  )
}
